import React, { useCallback, useEffect } from 'react';
import c from 'classnames';

import { Typography } from '@lyearn/ui';

import useResizableTextArea from '../../../../../hooks/useResizableTextArea';

// Character limit is used for restricting excess of copy-pasting.
const FlashCardTextArea: React.FC<{
  className?: string;
  placeholder?: string;
  readOnly: boolean;
  text?: string;
  onTextChange: (newText: string) => void;
  lineLimit: number;
  lineHeight: number;
  charLimit: number;
}> = (props) => {
  let { readOnly, text, onTextChange, className, placeholder, lineLimit, lineHeight, charLimit } =
    props;
  const { textAreaInputRef } = useResizableTextArea(text);

  const onChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      onTextChange(event.target.value);
    },
    [onTextChange],
  );

  const onPaste = useCallback(
    (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const inputField = event.clipboardData.getData('text').replace(/\n/g, ' ');
      if (inputField.length + (text?.length || 0) <= charLimit) {
        onTextChange(text + inputField);
      }
      event.preventDefault();
    },
    [charLimit, onTextChange, text],
  );

  useEffect(() => {
    if (
      text &&
      textAreaInputRef.current &&
      textAreaInputRef.current.clientHeight > lineHeight * lineLimit
    ) {
      onTextChange(text.substring(0, text.length - 1));
    }
  }, [lineHeight, lineLimit, onTextChange, text, textAreaInputRef]);

  if (readOnly) {
    return text ? (
      <Typography className={c(className, 'text-start')} variant="body-long-01">
        {text}
      </Typography>
    ) : null;
  }

  return (
    <textarea
      ref={textAreaInputRef}
      className={c(className, 'resize-none outline-none')}
      placeholder={placeholder}
      value={text}
      onInput={onChange}
      onPaste={onPaste}
    />
  );
};

export default FlashCardTextArea;
